import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CardSearchBar } from '@/components/CardSearchBar';
import { matchesCard } from '@/lib/cardSearch';
import { api, artwork } from "@/lib/api";
import { CardTile, CardGrid } from "@/components/CardTile";
import { PageTitle, Spinner, Chips, Empty } from "@/components/UI";
import { CardView, FilterPanel, cardFields, filterCards } from '@/components/CardBrowser';
import { displayCards } from '@/lib/setCards';
import { useRememberedState } from '@/hooks/useRememberedState';

const GENS = [[1, 151], [152, 251], [252, 386], [387, 493], [494, 649], [650, 721], [722, 809], [810, 905], [906, 1025]];
const cap = (s) => s.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

export function Pokedex() {
  const [list, setList] = useState(null);
  const [q, setQ] = useRememberedState('search', '');
  const [gen, setGen] = useRememberedState('generation', '');
  useEffect(() => { api.get("/pokedex").then((r) => setList(r.data)); }, []);
  const shown = useMemo(() => {
    if (!list) return [];
    const [a, b] = GENS[Number(gen) - 1] || (gen === '10' ? [1026, Infinity] : [1, Infinity]);
    return list.filter(p => p.id >= a && p.id <= b && matchesCard({ ...p, localId: String(p.id) }, q));
  }, [list, q, gen]);
  if (!list) return <Spinner text="Loading Pokédex…" />;
  const opts = GENS.map((_, i) => ({ value: String(i + 1), label: `Gen ${i + 1}` }));
  if (list.some((p) => p.id > 1025)) opts.push({ value: "10", label: "New" });
  return (
    <div data-testid="pokedex-page">
      <PageTitle title="Pokédex" sub={`All ${list.length} Pokémon. Tap one to see every card ever made of it.`} />
      <div className="mb-6 space-y-3">
        <CardSearchBar value={q} onChange={setQ} placeholder="Search Pokémon by name or number… -term excludes" testid="pokedex-search-input" />
        <details className="rounded-xl border bg-white p-3"><summary className="cursor-pointer text-sm font-bold">{gen ? `Generation ${gen}` : 'All generations'} · Filter</summary><div className="mt-3 flex flex-wrap gap-2">{opts.map(option => <button key={option.value} className={`rounded-full border px-3 py-2 text-xs ${gen === option.value ? 'bg-slate-900 text-white' : ''}`} onClick={() => setGen(gen === option.value ? '' : option.value)}>{option.label}</button>)}<button className="px-3 text-xs font-bold" onClick={() => setGen('')}>All generations</button></div></details>
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
        {shown.map((p) => (
          <Link key={p.id} to={`/pokedex/${p.id}`} data-testid={`pokedex-entry-${p.id}`} className="group rounded-2xl border border-slate-200/60 bg-white p-2 text-center transition-transform hover:-translate-y-1">
            <img src={artwork(p.id)} alt={p.name} loading="lazy" className="mx-auto aspect-square w-full object-contain transition-transform group-hover:scale-110" />
            <p className="font-mono text-[10px] text-slate-400">#{String(p.id).padStart(4, "0")}</p>
            <p className="truncate font-display text-sm font-bold">{cap(p.name)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function PokemonCards() {
  const { dex } = useParams();
  const [cards, setCards] = useState(null);
  const [name, setName] = useState("");
  const [filter, setFilter] = useRememberedState('filters', {});
  useEffect(() => {
    setCards(null);
    const controller = new AbortController();
    const load = async () => {
      const all = [];
      let page = 1, more = true;
      while (more && !controller.signal.aborted) {
        const { data } = await api.get('/cards', { params: { dex, size: 250, page }, signal: controller.signal });
        if (controller.signal.aborted) return;
        if (data.preparing) { await new Promise(resolve => setTimeout(resolve, 1000)); continue; }
        all.push(...data.items); more = data.has_more; page++;
      }
      if (!controller.signal.aborted) setCards(all);
    };
    load().catch(() => { if (!controller.signal.aborted) setCards([]); });
    api.get("/pokedex").then((r) => setName(cap(r.data.find((p) => String(p.id) === dex)?.name || "")));
    return () => controller.abort();
  }, [dex]);
  const owned = cards?.filter((c) => c.owned).length || 0;
  return <CardView view="pokemon" controls={false}>{({ display, editButton, editor }) => (
    <div data-testid="pokemon-cards-page">
      <div className="mb-8 flex items-center gap-6">
        <img src={artwork(dex)} alt={name} className="h-28 w-28 object-contain sm:h-36 sm:w-36" />
        <div>
          <p className="font-mono text-sm text-slate-500">#{String(dex).padStart(4, "0")}</p>
          <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">{name}</h1>
          {cards && <p className="mt-1 text-slate-600" data-testid="pokemon-owned-summary">You own {owned} of {cards.length} {name} cards</p>}
        </div>
        {editButton}
      </div>
      {editor}
      <FilterPanel value={filter} onChange={setFilter} fields={cardFields(cards || [])} />
      {!cards ? <Spinner /> : cards.length === 0 ? <Empty title="No cards yet" text="This Pokémon hasn't appeared in the TCG yet." /> : (
        <CardGrid testid="pokemon-cards-grid">{filterCards(displayCards(cards, display), filter, { expandPrices: false }).map((c, i) => <CardTile key={c.key || c.id} card={c} variant={c.variant} owned={c.owned} missing={!c.owned} index={i % 30} />)}</CardGrid>
      )}
    </div>
  )}</CardView>;
}
